<script setup lang="ts">
import type { BankAccount } from '@/types/UserData';
import {
  getBankAccountTypeLabel,
  joinDigit,
} from '@/utils/bank-account-labels';

type Props = {
  // Minha Conta V2 (ScreenBankAccounts.vue) embute este componente antigo,
  // mas quer a cor/raio na paleta v2 (escura), não o azul claro do site
  // antigo.
  variant?: 'default' | 'v2';
};

const { variant = 'default' } = defineProps<Props>();

const { ApiService } = useApiService();
const { openToaster } = useLayoutStore();
const { modalType } = storeToRefs(useLayoutStore());
const { isAuthenticated } = storeToRefs(useAuthStore());
const { t } = useTexts();
const { $containment } = useNuxtApp();
const { fetchUserProfile } = useUserData();

const MAX_ACCOUNTS = 3;

const accounts = ref<BankAccount[]>([]);
const isLoading = ref(false);
const isChoiceModalOpen = ref(false);
const isEditModalOpen = ref(false);
const editingAccount = ref<BankAccount | null>(null);
const pendingDeleteId = ref<number | null>(null);

// PRD C1/A6: só as contas "completas" (banco+agência+conta) são a conta do
// pagador desta seção — chave PIX (key_pix sem esses campos) é gerida na
// seção de PIX, que já existe nesta mesma página.
const payerAccounts = computed(() =>
  accounts.value.filter(
    (account) => account.agency && account.number && account.bank_id,
  ),
);

const isLimitReached = computed(
  () => payerAccounts.value.length >= MAX_ACCOUNTS,
);

// Mostra só as 3 primeiras por padrão (mesmo com mais contas cadastradas
// que o limite atual permite — ex.: dado legado) — "Ver mais" revela o
// resto, evitando lista longa por padrão na tela de Dados da Conta.
const VISIBLE_ACCOUNTS_COUNT = 3;
const isExpanded = ref(false);

const visibleAccounts = computed(() =>
  isExpanded.value
    ? payerAccounts.value
    : payerAccounts.value.slice(0, VISIBLE_ACCOUNTS_COUNT),
);

const hasHiddenAccounts = computed(
  () =>
    !isExpanded.value && payerAccounts.value.length > VISIBLE_ACCOUNTS_COUNT,
);

function toggleExpanded() {
  isExpanded.value = !isExpanded.value;
}

const isInitialLoading = computed(
  () => isLoading.value && !accounts.value.length,
);

async function fetchAccounts() {
  isLoading.value = true;

  try {
    const { data } = await ApiService.get<BankAccount[]>(
      '/bff/users/bank-account',
    );

    accounts.value = data ?? [];
  } catch (err) {
    console.error('payer-accounts: fetching', err);

    openToaster({
      message: t('bank_account.payer_accounts.fetch_error'),
      type: 'error',
    });
  } finally {
    isLoading.value = false;
  }
}

onMounted(fetchAccounts);

watch(modalType, (type, previousType) => {
  if (previousType === 'deposit' && type !== 'deposit') {
    fetchAccounts();
  }
});

// Bug real: diferente de "Documentos"/"Seus dados" (computed direto da
// store useUserDataStore, que se limpa sozinha no userData.$reset() do
// logout), `accounts` é um ref local alimentado só por fetch — o logout
// nunca avisava este componente, então a lista ficava visível depois de
// deslogar.
watch(isAuthenticated, (authenticated) => {
  if (!authenticated) {
    accounts.value = [];
  }
});

function onAddClick() {
  isChoiceModalOpen.value = true;
}

function onEditClick(account: BankAccount) {
  editingAccount.value = account;
  isEditModalOpen.value = true;
}

function onRemoveClick(id: number) {
  pendingDeleteId.value = id;
}

function cancelRemove() {
  pendingDeleteId.value = null;
}

async function confirmRemove(id: number) {
  await $containment.handle('update_data', () => {
    deleteAccount(id);
  });
}

async function deleteAccount(id: number) {
  const { $kyc } = useNuxtApp();
  const { temporaryPassword } = useModulesValidation();

  isLoading.value = true;

  // Mesmo padrão de ManualAccountModal.vue.saveAccount(): o containment só
  // abre o modal de senha/KYC — quem lê o resultado e manda no request é
  // aqui. ApiService.delete aceita body igual PATCH/POST
  // (_cactus_core/ApiService/index.ts:411-419).
  const payload = {
    ...($kyc?.current?.id ? { kyc_id: $kyc.current.id } : {}),
    ...(temporaryPassword.get()
      ? { password_check: temporaryPassword.get() }
      : {}),
  };

  try {
    await ApiService.delete(`/bff/users/bank-account/${id}`, payload);
    await fetchAccounts();

    // has_bank_account vem do profile, não da lista de contas — sem isso,
    // deletar a última conta só reflete depois de um F5. Best-effort: a
    // remoção já aconteceu no passo acima, uma falha aqui não é "erro ao
    // remover".
    fetchUserProfile({ force: true }).catch((err) => {
      console.error('payer-accounts: refreshing profile', err);
    });

    openToaster({
      message: t('bank_account.payer_accounts.remove_success'),
      type: 'success',
    });
  } catch (err: any) {
    console.error('payer-accounts: removing', err);

    // Guia PIX §6: 404 = "id não é do jogador" — a conta já não existe
    // mais como a UI supõe, então vale atualizar a lista mesmo no erro.
    if (err?.status === 404) {
      await fetchAccounts();
    }

    openToaster({
      message: getBankAccountErrorMessage(
        err,
        t,
        t('bank_account.payer_accounts.remove_error'),
      ),
      type: 'error',
    });
  } finally {
    isLoading.value = false;
    pendingDeleteId.value = null;
    temporaryPassword.clear();
  }
}

function agencyAccountLabel(account: BankAccount) {
  return t('bank_account.payer_accounts.agency_account', {
    agency: joinDigit(account.agency, account.agency_digit),
    number: joinDigit(account.number, account.number_digit),
  });
}
</script>

<template>
  <ClientOnly>
    <div :class="$style.container">
      <span
        :class="[
          $style.sectionLabel,
          { [$style.sectionLabelV2]: variant === 'v2' },
        ]"
      >
        {{ $t('bank_account.payer_accounts.title') }}
      </span>

      <BaseLoading
        :full-screen="false"
        inline
        :show="isInitialLoading"
        size="small"
      />

      <template v-if="!isInitialLoading">
        <p
          v-if="!payerAccounts.length"
          :class="$style.empty"
        >
          {{ $t('bank_account.payer_accounts.empty') }}
        </p>

        <div
          v-for="account in visibleAccounts"
          :key="account.id"
          :class="[$style.card, { [$style.cardV2]: variant === 'v2' }]"
        >
          <div :class="$style.cardHeader">
            <span
              :class="[
                $style.bankName,
                { [$style.bankNameV2]: variant === 'v2' },
              ]"
            >
              {{ account.bank_name || account.bank_id }}
            </span>
            <span
              :class="[$style.badge, { [$style.badgeV2]: variant === 'v2' }]"
            >
              {{ $t('bank_account.payer_accounts.badge_registered') }}
            </span>

            <div :class="$style.cardActions">
              <button
                :aria-label="$t('bank_account.payer_accounts.edit')"
                type="button"
                @click="onEditClick(account)"
              >
                <NuxtIcon name="user/edit" />
              </button>
              <button
                :aria-label="$t('bank_account.payer_accounts.remove')"
                type="button"
                @click="onRemoveClick(account.id!)"
              >
                <svg
                  fill="none"
                  height="14"
                  viewBox="0 0 14 14"
                  width="14"
                >
                  <path
                    d="M2 4h10M5.5 4V2.5A1 1 0 016.5 1.5h1a1 1 0 011 1V4M5.5 6.5v4M8.5 6.5v4M3 4l.5 8a1 1 0 001 1h5a1 1 0 001-1l.5-8"
                    stroke="#FF5454"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="1.2"
                  />
                </svg>
              </button>
            </div>
          </div>

          <p :class="$style.accountLine">
            {{ agencyAccountLabel(account) }}
          </p>
          <p
            v-if="account.type"
            :class="$style.typeLine"
          >
            {{ getBankAccountTypeLabel(account.type, t) }}
          </p>

          <div
            v-if="pendingDeleteId === account.id"
            :class="$style.confirmRow"
          >
            <span>{{
              $t('bank_account.payer_accounts.remove_confirm_title')
            }}</span>
            <div :class="$style.confirmActions">
              <button
                :class="$style.confirmYes"
                :disabled="isLoading"
                type="button"
                @click="confirmRemove(account.id!)"
              >
                {{ $t('bank_account.payer_accounts.remove_confirm_yes') }}
              </button>
              <button
                :class="$style.confirmNo"
                :disabled="isLoading"
                type="button"
                @click="cancelRemove"
              >
                {{ $t('bank_account.payer_accounts.remove_confirm_no') }}
              </button>
            </div>
          </div>
        </div>
      </template>

      <button
        v-if="hasHiddenAccounts"
        :class="[
          $style.showMoreButton,
          { [$style.showMoreButtonV2]: variant === 'v2' },
        ]"
        type="button"
        @click="toggleExpanded"
      >
        {{ $t('bank_account.payer_accounts.show_more') }}
      </button>
      <button
        v-else-if="isExpanded && payerAccounts.length > VISIBLE_ACCOUNTS_COUNT"
        :class="[
          $style.showMoreButton,
          { [$style.showMoreButtonV2]: variant === 'v2' },
        ]"
        type="button"
        @click="toggleExpanded"
      >
        {{ $t('bank_account.payer_accounts.show_less') }}
      </button>

      <p
        v-if="isLimitReached"
        :class="$style.limitNotice"
      >
        {{ $t('bank_account.payer_accounts.limit_reached') }}
      </p>
      <button
        v-else
        :class="[$style.addButton, { [$style.addButtonV2]: variant === 'v2' }]"
        type="button"
        @click="onAddClick"
      >
        {{ $t('bank_account.payer_accounts.add_button') }}
      </button>
    </div>

    <BankAddAccountChoiceModal
      v-model="isChoiceModalOpen"
      :variant="variant"
      @saved="fetchAccounts"
    />

    <BankManualAccountModal
      v-model="isEditModalOpen"
      :edit-account="editingAccount"
      :variant="variant"
      @saved="fetchAccounts"
    />
  </ClientOnly>
</template>

<style module lang="postcss">
.container {
  @apply flex flex-col gap-3;
}

.sectionLabel {
  @apply text-[10px] font-semibold text-primary uppercase;

  /* mesmo tom neutro dos rótulos de seção do resto do painel v2 (ex.:
     ScreenAccountData.vue), no lugar do azul do site antigo */
  &.sectionLabelV2 {
    @apply text-white/60;
  }
}

.empty {
  @apply text-xs text-texts/50;
}

.card {
  @apply flex flex-col gap-1 p-3 rounded-lg border border-solid border-success/40 bg-success/[0.05];

  /* mesmo card neutro usado no resto do painel v2 (border-v2-default/40 +
     bg-v2-default/40), no lugar do verde do site antigo */
  &.cardV2 {
    @apply rounded-[12px] border-v2-default/40 bg-v2-default/40;
  }
}

.cardHeader {
  @apply flex items-center gap-2;
}

.bankName {
  @apply text-primary text-xs font-semibold;

  &.bankNameV2 {
    @apply text-white;
  }
}

.badge {
  @apply px-1.5 py-0.5 rounded text-[9px] font-semibold text-white;
  background: radial-gradient(
    168.3% 138% at 36.76% -16.67%,
    #73dd3e 0%,
    #2b8b1a 100%
  );

  &.badgeV2 {
    @apply rounded-[6px] bg-v2-success bg-none;
  }
}

.cardActions {
  @apply flex items-center gap-2 ml-auto;

  button {
    @apply flex items-center justify-center w-6 h-6 text-texts/60;
  }
}

.accountLine {
  @apply text-sm text-texts font-medium;
}

.typeLine {
  @apply text-xs text-texts/50;
}

.confirmRow {
  @apply flex flex-col gap-2 mt-2 pt-2 border-t border-solid border-white/10 text-xs text-texts/80;
}

.confirmActions {
  @apply flex gap-2;
}

.confirmYes {
  @apply px-3 py-1.5 rounded-md bg-error text-white text-xs font-semibold;

  &:disabled {
    @apply opacity-[0.35] cursor-default;
  }
}

.confirmNo {
  @apply px-3 py-1.5 rounded-md border border-solid border-texts/30 text-texts/70 text-xs font-semibold;

  &:disabled {
    @apply opacity-[0.35] cursor-default;
  }
}

.addButton {
  @apply w-full h-11 rounded-lg border border-solid border-primary/40 text-primary text-xs font-semibold uppercase;

  /* mesmo botão primário escuro do resto do painel v2 (Figma 19831:60693):
     preenchido, sem uppercase, peso normal — não o outline azul claro */
  &.addButtonV2 {
    @apply h-[40px] rounded-[12px] border-0 bg-v2-primary text-[14px] font-normal normal-case text-v2-primary-foreground;
  }
}

.showMoreButton {
  @apply w-full h-11 rounded-lg border border-solid border-primary/40 text-primary text-xs font-semibold uppercase;

  &.showMoreButtonV2 {
    @apply h-[40px] rounded-[12px] border-v2-default-500 bg-v2-default-300 text-[14px] font-normal normal-case text-white;
  }
}

.limitNotice {
  @apply text-xs text-warning;
}
</style>
